import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';

interface KernelCrashAlertProps {
    isVisible: boolean;
    onComplete: () => void;
}

export const KernelCrashAlert: React.FC<KernelCrashAlertProps> = ({ isVisible, onComplete }) => {
    const { t } = useLanguage();
    const [phase, setPhase] = useState<'alert' | 'shutdown'>('alert');

    useEffect(() => {
        if (!isVisible) return;

        // Phase 1: Alerte critique (3 secondes)
        const alertTimer = setTimeout(() => {
            setPhase('shutdown');
            // Déclencher l'animation de shutdown avec flag spécial pour kernel crash
            window.dispatchEvent(new CustomEvent('SYSTEM_SHUTDOWN_REQUESTED', { 
                detail: { isKernelCrash: true } 
            }));
            onComplete(); // Fermer l'alerte, le shutdown prend le relais
        }, 3000);

        return () => {
            clearTimeout(alertTimer);
        };
    }, [isVisible, onComplete]);

    if (!isVisible || phase === 'shutdown') return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center pointer-events-none">
            {/* Overlay semi-transparent */}
            <div className="absolute inset-0 bg-black/50"></div>
            
            {/* Fenêtre d'alerte critique */}
            <div className="relative bg-red-600 border-4 border-red-400 shadow-2xl shadow-red-900/50 w-96 pointer-events-auto animate-pulse">
                {/* Barre de titre agressive */}
                <div className="bg-red-800 border-b-2 border-red-400 p-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-400 animate-ping"></div>
                        <span className="text-white font-bold text-sm uppercase tracking-wider">{t('kernelCrash.title')}</span>
                    </div>
                    <div className="text-white text-lg animate-bounce">⚠️</div>
                </div>

                {/* Contenu de l'alerte */}
                <div className="p-4 text-center text-white">
                    <div className="text-4xl mb-3 animate-bounce">💀</div>
                    <div className="text-xl font-bold mb-2 animate-pulse">{t('kernelCrash.processTerminated')}</div>
                    <div className="text-sm font-mono mb-3 bg-black/30 p-2 border border-red-400">
                        {t('kernelCrash.kernelFile')}
                    </div>
                    <div className="text-base font-bold mb-2">{t('kernelCrash.forcedShutdown')}</div>
                    <div className="text-sm opacity-90">{t('kernelCrash.systemRestarting')}</div>
                </div>

                {/* Barre de progression agressive */}
                <div className="bg-red-800 p-2">
                    <div className="bg-red-900 h-2 rounded overflow-hidden">
                        <div className="bg-yellow-400 h-full w-0 animate-pulse transition-all duration-[3000ms] ease-linear" 
                             style={{ width: '100%' }}></div>
                    </div>
                </div>

                {/* Effets visuels stressants */}
                <div className="absolute inset-0 border-2 border-white animate-ping opacity-50"></div>
                <div className="absolute -inset-1 border border-red-300 animate-pulse"></div>
            </div>
        </div>
    );
};